import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, ArrowRight, Plus, Send, Users, Hash, CheckSquare, Trash2, LogOut } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Assignments() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cases, setCases] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [formData, setFormData] = useState({
    task_type: 'معاملة',
    case_id: '',
    consultation_id: '',
    assigned_to: [],
    assigned_to_names: [],
    instructions: '',
  });
  const [responseData, setResponseData] = useState({
    response: '',
    status: 'completed',
    reassign_to: '',
    reassign_to_name: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [assignmentsRes, employeesRes, casesRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/assignments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/cases`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAssignments(assignmentsRes.data);
      setEmployees(employeesRes.data);
      setCases(casesRes.data);
      setConsultations(consultationsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assignments`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchData();
      setFormData({
        task_type: 'معاملة',
        case_id: '',
        consultation_id: '',
        assigned_to: [],
        assigned_to_names: [],
        instructions: '',
      });
    } catch (err) {
      console.error('Error creating assignment:', err);
    }
  };

  const handleResponse = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/assignments/${selectedAssignment.id}`, responseData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowResponseModal(false);
      setSelectedAssignment(null);
      fetchData();
      setResponseData({
        response: '',
        status: 'completed',
        reassign_to: '',
        reassign_to_name: '',
      });
    } catch (err) {
      console.error('Error updating assignment:', err);
    }
  };

  const handleDeleteAssignment = async () => {
    if (!selectedAssignment) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assignments/${selectedAssignment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteConfirm(false);
      setSelectedAssignment(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      alert('حدث خطأ أثناء حذف المهمة');
    }
  };

  const handleEmployeeToggle = (employee) => {
    const isSelected = formData.assigned_to.includes(employee.id);
    if (isSelected) {
      setFormData({
        ...formData,
        assigned_to: formData.assigned_to.filter(id => id !== employee.id),
        assigned_to_names: formData.assigned_to_names.filter(name => name !== employee.full_name),
      });
    } else {
      setFormData({
        ...formData,
        assigned_to: [...formData.assigned_to, employee.id],
        assigned_to_names: [...formData.assigned_to_names, employee.full_name],
      });
    }
  };

  const handleReassignChange = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    if (employee) {
      setResponseData({
        ...responseData,
        reassign_to: employeeId,
        reassign_to_name: employee.full_name,
      });
    }
  };

  const canRespond = (assignment) => {
    if (!user) return false;
    const assignedToList = assignment.assigned_to || [];
    return assignedToList.includes(user.id) && assignment.status === 'pending';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">المهام المحالة</h1>
          </div>
          <div className="flex gap-3">
            {user?.role !== 'client' && (
              <button
                data-testid="add-assignment-btn"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                إحالة مهمة
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <div
              key={assignment.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100"
              data-testid={`assignment-card-${assignment.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <Briefcase className="w-8 h-8 text-gold mt-1" />
                  <div className="flex-1">
                    {/* رقم المهمة */}
                    {assignment.assignment_number && (
                      <div className="flex items-center gap-2 mb-2 text-sm text-gold">
                        <Hash className="w-4 h-4" />
                        <span className="font-body font-semibold">{assignment.assignment_number}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-heading font-semibold text-primary">
                        {assignment.task_type || 'مهمة'}
                      </h3>
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded font-body">
                        {assignment.task_type}
                      </span>
                    </div>
                    
                    {/* الموظفون المسندون */}
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <p className="text-sm text-gray-600 font-body">
                        <strong>محال إلى:</strong>{' '}
                        {assignment.assigned_to_names?.length > 0 
                          ? assignment.assigned_to_names.join('، ') 
                          : assignment.assigned_to_name || 'غير محدد'}
                      </p>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 font-body">
                      <strong>محال من:</strong> {assignment.assigned_by_name}
                    </p>
                    <p className="text-sm text-gray-700 mb-3 font-body">
                      <strong>التعليمات:</strong> {assignment.instructions}
                    </p>

                    {assignment.response && (
                      <div className="mt-4 p-4 bg-sand rounded-md">
                        <p className="text-sm font-semibold text-primary mb-2 font-body">الرد:</p>
                        <p className="text-sm text-gray-700 font-body">{assignment.response}</p>
                      </div>
                    )}

                    {canRespond(assignment) && (
                      <button
                        data-testid={`respond-assignment-${assignment.id}`}
                        onClick={() => {
                          setSelectedAssignment(assignment);
                          setShowResponseModal(true);
                        }}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
                      >
                        <Send className="w-4 h-4" />
                        الرد على المهمة
                      </button>
                    )}
                    
                    {/* زر الحذف للمدير */}
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => {
                          setSelectedAssignment(assignment);
                          setShowDeleteConfirm(true);
                        }}
                        className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-all duration-300"
                        data-testid={`delete-assignment-${assignment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف
                      </button>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body whitespace-nowrap ${
                    assignment.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : assignment.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : assignment.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {assignment.status === 'pending' ? 'معلقة' : 
                   assignment.status === 'completed' ? 'مكتملة' : 
                   assignment.status === 'in_progress' ? 'قيد التنفيذ' : assignment.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {assignments.length === 0 && (
          <div className="text-center py-16" data-testid="no-assignments">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد مهام محالة حالياً</p>
          </div>
        )}
      </main>

      {/* نافذة تأكيد الحذف */}
      {showDeleteConfirm && selectedAssignment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" data-testid="delete-confirm-modal">
            <h2 className="text-xl font-heading font-bold text-red-600 mb-4">تأكيد حذف المهمة</h2>
            <div className="bg-red-50 p-3 rounded-lg mb-4">
              <p className="font-body font-semibold text-red-800">{selectedAssignment.assignment_number}</p>
              <p className="text-sm text-red-600">{selectedAssignment.task_type} - {selectedAssignment.instructions?.substring(0, 50)}...</p>
            </div>
            <p className="text-sm text-red-500 font-body mb-4">
              ⚠️ هذا الإجراء لا يمكن التراجع عنه
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => { setShowDeleteConfirm(false); setSelectedAssignment(null); }}
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteAssignment}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all font-body"
              >
                حذف نهائي
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Assignment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-testid="assignment-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">إحالة مهمة جديدة</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  نوع المهمة
                </label>
                <select
                  data-testid="assignment-task-type"
                  value={formData.task_type || 'معاملة'}
                  onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="معاملة">معاملة</option>
                  <option value="مراجعة">مراجعة</option>
                  <option value="تواصل">تواصل</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  نوع الخدمة
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  onChange={(e) => {
                    if (e.target.value === 'case') {
                      setFormData({ ...formData, consultation_id: '', case_id: '' });
                    } else {
                      setFormData({ ...formData, case_id: '', consultation_id: '' });
                    }
                  }}
                >
                  <option value="case">قضية</option>
                  <option value="consultation">استشارة</option>
                </select>
              </div>

              {/* اختيار الموظفين - متعدد */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  <Users className="w-4 h-4 inline-block ml-1" />
                  الموظفون المسند إليهم (يمكن اختيار أكثر من موظف)
                </label>
                <div className="border border-gray-200 rounded-md p-4 max-h-48 overflow-y-auto">
                  {employees.map((emp) => (
                    <label
                      key={emp.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-sand transition-colors ${
                        formData.assigned_to.includes(emp.id) ? 'bg-gold/10' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.assigned_to.includes(emp.id)}
                        onChange={() => handleEmployeeToggle(emp)}
                        className="w-4 h-4 text-gold"
                      />
                      <span className="font-body">{emp.full_name}</span>
                      <span className="text-xs text-gray-500 font-body">- {emp.role === 'lawyer' ? 'محامي' : emp.role === 'accountant' ? 'محاسب' : 'موظف'}</span>
                    </label>
                  ))}
                </div>
                {formData.assigned_to.length > 0 && (
                  <p className="mt-2 text-sm text-gold font-body">
                    <CheckSquare className="w-4 h-4 inline-block ml-1" />
                    تم اختيار {formData.assigned_to.length} موظف: {formData.assigned_to_names.join('، ')}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  التعليمات
                </label>
                <textarea
                  data-testid="assignment-instructions"
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="اكتب التعليمات والإجراءات المطلوبة من الموظف..."
                />
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="assignment-submit"
                  type="submit"
                  disabled={formData.assigned_to.length === 0}
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  إحالة
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4" data-testid="response-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">الرد على المهمة</h2>
            <form onSubmit={handleResponse} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  الرد
                </label>
                <textarea
                  data-testid="response-text"
                  value={responseData.response}
                  onChange={(e) => setResponseData({ ...responseData, response: e.target.value })}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="اكتب ردك على المهمة..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  الحالة
                </label>
                <select
                  data-testid="response-status"
                  value={responseData.status}
                  onChange={(e) => setResponseData({ ...responseData, status: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="completed">مكتملة</option>
                  <option value="in_progress">قيد التنفيذ</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  إحالة إلى موظف آخر (اختياري)
                </label>
                <select
                  data-testid="reassign-employee"
                  onChange={(e) => handleReassignChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="">لا توجد إحالة</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name} - {emp.role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedAssignment(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="response-submit"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  إرسال
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
